import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { SharedModule } from '@app/shared/shared.module';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { ScopeUiInputComponent } from '@shared/components/ui-components/scope-ui-input/scope-ui-input.component';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import {
  ScopeUiDropdownComponent,
} from '@shared/components/ui-components/scope-ui-dropdown/scope-ui-dropdown.component';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  SNACKBAR_LENGTH_LONG,
  SNACKBAR_LENGTH_SHORT,
  SnackbarEventType,
  SnackbarService,
} from '@shared/utils/snackbar.service';
import {
  DeliverableDynamicFieldSettingModel,
  DynamicFieldType,
} from '@app/features/scoping/models/scope-dynamic-field-setting.model';
import { ScopeActions } from '@app/features/scoping/store/actions/scoping.actions';
import { UserSettingsService } from '@app/features/scope-overview/service/user-settings.service';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { generateFieldId } from '@shared/utils/utils';
import {
  DeliverableDynamicFieldSettingService
} from '@app/features/scoping/service/deliverable-dynamic-field-setting.service';

export interface DeliverableDynamicFieldSettingModalConfig {
  title: string;
  submitButtonName: string;
  deliverableDynamicFieldSetting: DeliverableDynamicFieldSettingModel;
  isEditModal: boolean;
}

@Component({
  selector: 'create-deliverable-dynamic-field-setting-modal',
  standalone: true,
  imports: [CommonModule, SharedModule, MatDatepickerModule, CKEditorModule, ScopeUiInputComponent, NgxSliderModule, ScopeUiDropdownComponent, NgxJsonViewerModule],
  templateUrl: './create-deliverable-dynamic-field-setting-modal.component.html',
  styleUrls: ['./create-deliverable-dynamic-field-setting-modal.component.scss'],
})
export class CreateDeliverableDynamicFieldSettingModalComponent implements OnInit, AfterViewInit {
  dynamicFieldTypeDropdownOptions: { name: string, id: DynamicFieldType }[] = [];
  companyApiIntegrationsDropdownOptions: { id: number, name: string }[] = [];
  companyApiIntegrations: any = [];
  selectedCompanyApiIntegration: { name: string, id: number };
  fieldNameControl = new FormControl(this.data.deliverableDynamicFieldSetting?.displayName);
  valueControl = new FormControl(this.data.deliverableDynamicFieldSetting?.value);
  resourceControl = new FormControl(this.data.deliverableDynamicFieldSetting?.externalSystemResourceUri);
  jsonValueNameControl = new FormControl(this.data.deliverableDynamicFieldSetting?.jsonValueName);
  fieldJsonKeyControl = new FormControl(this.data.deliverableDynamicFieldSetting?.name);
  mandatoryControl = new FormControl(this.data.deliverableDynamicFieldSetting?.mandatory);
  jsonNode: any;
  isFetchingExternalData: boolean = false;


  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DeliverableDynamicFieldSettingModalConfig,
    private deliverableDynamicFieldSettingService: DeliverableDynamicFieldSettingService,
    private store: Store,
    private snackbarService: SnackbarService,
    public dialog: MatDialog,
    public userSettingsService: UserSettingsService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    if (!this.data.isEditModal) {
      this.data.deliverableDynamicFieldSetting = new DeliverableDynamicFieldSettingModel();
    }
    this.dynamicFieldTypeDropdownOptions = Object.values(DynamicFieldType).filter(type => type !== 'DROPDOWN_EXTERNAL').map(type => ({
      name: type,
      id: type,
    }));

    if (this.data.deliverableDynamicFieldSetting && this.data.deliverableDynamicFieldSetting.type === 'DROPDOWN_EXTERNAL') {
      this.getCompanyApiIntegration(this.data.deliverableDynamicFieldSetting.companyApiIntegrationId);
      const updatedSetting = { ...this.data.deliverableDynamicFieldSetting };
      updatedSetting.jsonValueName = updatedSetting.jsonValueName.replace(/^\$\./, '');
      this.data.deliverableDynamicFieldSetting = updatedSetting;
    }

    this.fieldNameControl.valueChanges.subscribe(value => {
      this.onFieldNameChange(value);
    });
    this.valueControl.valueChanges.subscribe(value => {
      this.onValueChange(value);
    });
    this.resourceControl.valueChanges.subscribe(value => {
      this.onResourceChange(value);
    });
    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  onFieldNameChange(value: string) {
    const updatedSetting = { ...this.data.deliverableDynamicFieldSetting };
    updatedSetting.displayName = value;
    if (value) {
      let fieldJsonKey = generateFieldId(value);
      this.fieldJsonKeyControl.setValue(fieldJsonKey);
      updatedSetting.name = fieldJsonKey;
    } else {
      this.fieldJsonKeyControl.setValue('');
    }
    this.data.deliverableDynamicFieldSetting = updatedSetting;
    this.cdr.detectChanges();
  }

  onValueChange(value: string) {
    const clonedSetting = { ...this.data.deliverableDynamicFieldSetting };
    clonedSetting.value = value;
    clonedSetting.defaultValue = value;
    this.data.deliverableDynamicFieldSetting = clonedSetting;
  }

  onResourceChange(value: string) {
    this.data.deliverableDynamicFieldSetting.externalSystemResourceUri = value;
  }

  onSelectionDynamicFieldType(event: any) {
    if (!event.id) return;
    if (event.id === 'DROPDOWN_EXTERNAL') {
      this.getCompanyApiIntegrations();
    }
    this.valueControl.reset();
    this.resourceControl.reset();
    this.jsonNode = null;
    this.data.deliverableDynamicFieldSetting.type = event.id;
  }

  clearDropdownExternalData(): void {
    this.data.deliverableDynamicFieldSetting.companyApiIntegrationId = null;
    this.data.deliverableDynamicFieldSetting.jsonValueName = null;
    this.data.deliverableDynamicFieldSetting.externalSystemResourceUri = null;
    this.data.deliverableDynamicFieldSetting.apiIntegrationName = null;
  }

  onToggle(event: any) {
    this.data.deliverableDynamicFieldSetting.mandatory = event.checked
  }

  getCompanyApiIntegrations(): void {
    this.userSettingsService.getCompanyIntegrations().subscribe(response => {
      this.companyApiIntegrations = response;
      this.companyApiIntegrationsDropdownOptions = this.companyApiIntegrations.map(companyApiIntegration => ({
        name: companyApiIntegration.apiIntegration.name,
        id: companyApiIntegration.id,
      }));
    });
  }

  getCompanyApiIntegration(id: number) {
    this.userSettingsService.getCompanyIntegrations().subscribe(response => {
      this.companyApiIntegrations = response;
      this.companyApiIntegrationsDropdownOptions = this.companyApiIntegrations.map(companyApiIntegration => ({
        name: companyApiIntegration.apiIntegration.name,
        id: companyApiIntegration.id,
      }));
      this.selectedCompanyApiIntegration = this.companyApiIntegrationsDropdownOptions.find(companyApiIntegration => companyApiIntegration.id === id);
      this.data.deliverableDynamicFieldSetting.apiIntegrationName = this.selectedCompanyApiIntegration.name;
      this.fetchExternalData();
    });
  }

  onSelectionCompanyApiIntegration(event: any) {
    if (!event.id) return;
    this.data.deliverableDynamicFieldSetting.companyApiIntegrationId = event.id;
    this.data.deliverableDynamicFieldSetting.apiIntegrationName = event.name;
  }

  disableSubmit(): boolean {
    switch (this.data.deliverableDynamicFieldSetting?.type) {
      case DynamicFieldType.TEXT:
        return !this.fieldNameControl.getRawValue();
      case DynamicFieldType.DROPDOWN:
        return !this.fieldNameControl.getRawValue() || !this.valueControl.getRawValue();
      case DynamicFieldType.DROPDOWN_EXTERNAL:
        return !this.fieldNameControl.getRawValue() ||
          !this.data.deliverableDynamicFieldSetting?.companyApiIntegrationId ||
          !this.data.deliverableDynamicFieldSetting?.externalSystemResourceUri ||
          !this.jsonValueNameControl.getRawValue();
      default:
        return true;
    }
  }

  isDropdownExternal(): boolean {
    return this.data.deliverableDynamicFieldSetting?.type === 'DROPDOWN_EXTERNAL';
  }

  onJsonKeyClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target && target.classList.contains('segment-key')) {
      const key = target.textContent?.trim();
      let parentArraysLength = 0;
      let currentElement = target.parentElement;
      let pathSegments: string[] = [];

      while (currentElement && !currentElement.classList.contains('ngx-json-viewer')) {
        if (currentElement.classList.contains('segment-type-array')) {
          parentArraysLength++;
          if (parentArraysLength > 0) {
            pathSegments = [];
            break;
          }
        }

        let parentNode = currentElement.parentElement;
        while (parentNode && !parentNode.classList.contains('segment')) {
          parentNode = parentNode.parentElement;
        }

        const parentKeyElement = parentNode?.querySelector('.segment-key') as HTMLElement;
        if (parentKeyElement) {
          const parentKey: string = parentKeyElement.textContent?.trim();
          if (parentKey && isNaN(Number(parentKey))) {
            pathSegments.unshift(parentKey);
          }
        }
        currentElement = parentNode;
      }
      if (parentArraysLength === 0 && key) {
        const jsonPath = pathSegments.join('.');
        this.jsonValueNameControl.setValue(jsonPath);
        this.cdr.detectChanges();
      } else {
        this.jsonValueNameControl.setValue(null);
        this.jsonValueNameControl.setErrors({
          invalidValue: true,
          message: 'You can\'t choose a field from a nested array',
        });
        this.cdr.detectChanges();
      }
    }
  }


  fetchExternalData() {
    this.isFetchingExternalData = true;
    this.deliverableDynamicFieldSettingService.getExternalSystemResource({
      integrationName: this.data.deliverableDynamicFieldSetting.apiIntegrationName,
      resourceUri: this.data.deliverableDynamicFieldSetting.externalSystemResourceUri,
      fetchFirstPageOnly: true
    }).subscribe({
      next: (resp) => {
        this.jsonNode = resp.slice(0, 10);
        this.isFetchingExternalData = false;
      }, error: () => {
        this.jsonNode = null;
        this.jsonValueNameControl.setValue(null);
        this.isFetchingExternalData = false;
        this.snackbarService.showSnackbar('Error during getting External System Resource', SNACKBAR_LENGTH_LONG, SnackbarEventType.ERROR);
      }
    });
  }

  onSubmit() {
    if (this.data.deliverableDynamicFieldSetting.type === DynamicFieldType.DROPDOWN_EXTERNAL) {
      this.data.deliverableDynamicFieldSetting.jsonValueName = '$.' + this.jsonValueNameControl.getRawValue();
    } else {
      this.clearDropdownExternalData();
    }
    if (this.data.isEditModal) {
      this.deliverableDynamicFieldSettingService.update(this.data.deliverableDynamicFieldSetting.id, this.data.deliverableDynamicFieldSetting).subscribe({
        next: (resp) => {
          this.store.dispatch(ScopeActions.updateDeliverableDynamicFieldSettingSuccess({ setting: resp }));
          this.snackbarService.showSnackbar(`Deliverable Dynamic Field Setting ${resp.displayName} was successfully updated.`, SNACKBAR_LENGTH_SHORT, SnackbarEventType.SUCCESS);
        },
        error: () => this.snackbarService.showSnackbar('Error during saving Deliverable Dynamic Field Setting', SNACKBAR_LENGTH_LONG, SnackbarEventType.ERROR),
      });
    } else {
      this.deliverableDynamicFieldSettingService.create(this.data.deliverableDynamicFieldSetting).subscribe({
        next: (resp) => {
          this.store.dispatch(ScopeActions.createDeliverableDynamicFieldSettingSuccess({ setting: resp }));
          this.snackbarService.showSnackbar(`Deliverable Dynamic Field Setting ${resp.displayName} was successfully created.`, SNACKBAR_LENGTH_SHORT, SnackbarEventType.SUCCESS);
        },
        error: () => this.snackbarService.showSnackbar('Error during creating Deliverable Dynamic Field Setting', SNACKBAR_LENGTH_LONG, SnackbarEventType.ERROR),
      });
    }
    this.dialog.closeAll();
  }
}
